import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ReferenceLine } from 'recharts';
import { useMediaQuery } from 'react-responsive';
import { useEffect, useRef, useState } from 'react';
import { jsPDF } from 'jspdf';
import { svg2pdf } from 'svg2pdf.js';

interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
}

const renderCustomBarLabelCont = ({ x, y, index, width, height, bgColor, data }: { x: number; y: number; index: number; width: number; height: number; bgColor: string, data: any }) => {
  const value = data[index]?.contrib ? data[index].contrib.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00';

  return (
    <>
      <g transform={`translate(${(x + width / 2) - 40}, ${(y + height - 10) - 10})`} style={{ zIndex: "99" }}>
        <rect width={80} height={20} rx={5} fill={bgColor} opacity={0.8} />
        <text x={40} y={15} fill="white" textAnchor="middle" fontSize={12} fontWeight="bold"
          style={{ filter: 'drop-shadow(3px 3px 2px rgba(0, 0, 0, 0.8))' }}>
          {`R$ ${value}`}
        </text>
      </g>
    </>
  );
};

const renderCustomBarLabelRest = ({ x, y, index, width, height, bgColor, data }: { x: number; y: number; index: number; width: number; height: number; bgColor: string, data: any }) => {
  const value = data[index]?.restante ? data[index].restante.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00';
  return (
    <g transform={`translate(${(x + width / 2) - 40}, ${(y + height - 10) - 20})`} style={{ zIndex: "99" }}>
      <rect width={80} height={20} rx={5} fill={bgColor} opacity={0.8} />
      <text x={40} y={15} fill="white" textAnchor="middle" fontSize={12} fontWeight="bold"
        style={{ filter: 'drop-shadow(3px 3px 2px rgba(0, 0, 0, 0.8))' }}>
        {`R$ ${value}`}
      </text>
    </g>
  );
};

const CustomLabelGreenLine = ({ viewBox, total, width, height }: { viewBox: any, total: number, width: number; height: number }) => {
  const { x, y } = viewBox;
  const totalFormated = total ? total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
  return (

    <g transform={`translate(${(x) - 10}, ${(y) + 2})`} style={{ zIndex: "99" }}>
      <rect width={100} height={20} rx={5} fill={'#f1f0ef'} opacity={0.8} />
      <text
        x={5} // Início da linha
        y={Math.max(14, 1)} // Garante que o label não fique fora da área visível
        fill="green"
        fontSize={12}
        fontWeight="bold"
        textAnchor="start"
        style={{ filter: 'drop-shadow(3px 3px 2px rgba(255, 255, 255, 0.2))' }}
      >
        {`R$ ${totalFormated}`}
      </text>
    </g>

  );
};

const CustomLegend = (props: any) => {

  return (
    <>
      <div style={{ display: "flex", justifyContent: "center", gap: "20px", marginBottom: "10px", paddingTop: "20px" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div
            style={{
              width: 16,
              height: 16,
              backgroundColor: "#6a1b9a",
              borderRadius: 4,
              marginRight: 8,
            }}
          />
          <span style={{ fontSize: 12, fontWeight: "normal", color: "#333" }}>{"Contribuição anual ao Plano"}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div
            style={{
              width: 16,
              height: 16,
              backgroundColor: "#ff8f00",
              borderRadius: 4,
              marginRight: 8,
            }}
          />
          <span style={{ fontSize: 12, fontWeight: "normal", color: "#333" }}>{"Valor restante a aportar para aproveitar o máximo do Benefício Fiscal"}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div
            style={{
              width: 16,
              height: 16,
              backgroundColor: "green",
              borderRadius: 4,
              marginRight: 8,
            }}
          />
          <span style={{ fontSize: 12, fontWeight: "normal", color: "#333" }}>{"Aproveitamento Máximo do Benefício Fiscal"}</span>
        </div>
      </div>
    </>
  );
};

const ChartIR = (_data: any) => {

  const data = _data.data;
  const plano = _data.plano;
  const remuneracao = _data.remuneracao;
  const outrosRend = _data.outrosRend;

  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
  const chartRef = useRef<HTMLDivElement>(null); // Ref para o gráfico
  const [height, setHeight] = useState(600);

  const total = data ? data.reduce((sum: number, item: any) => sum + item.total, 0) / data.length : 0;

  useEffect(() => {
    if (!data) return;
    if (isMobile) {
      const timer = setTimeout(() => {
      }, 1000); // Ajuste o tempo conforme necessário

      return () => clearTimeout(timer);
    }
  }, [data, isMobile]);

  useEffect(() => {
    const updateHeight = () => {
      if (chartRef.current) {
        const newHeight = chartRef.current.clientHeight;
        setHeight(newHeight);
      }
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);

    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  const handleDownloadPDF = () => {
    const chartElement = chartRef.current;

    if (chartElement) {
      const svgElement = chartElement.querySelector('svg');

      if (svgElement) {
        const pdf = new jsPDF('landscape', 'pt', 'a4');

        addHeader(pdf);

        svg2pdf(svgElement, pdf, {
          x: (pdf.internal.pageSize.getWidth() - 500) / 2, // Centraliza horizontalmente
          y: 110, // Posiciona abaixo do cabeçalho
          width: 500, // Largura do SVG no PDF
          height: 300, // Altura do SVG no PDF
        }).then(() => {
          // Salva o PDF
          pdf.save(`grafico_deduzir_${new Date().getFullYear().toString()}_${new Date().getMonth().toString()}_${new Date().getDate().toString()}.pdf`);
        }).catch((error) => {
          console.error('Erro ao adicionar SVG ao PDF:', error);
        });
      } else {
        console.error('Elemento SVG não encontrado.');
      }
    } else {
      console.error('Elemento do gráfico não encontrado.');
    }
  };

  // Função para adicionar um cabeçalho ao PDF
  const addHeader = (pdf: jsPDF) => {
    const pageWidth = pdf.internal.pageSize.getWidth();

    // Adiciona um título
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text('FORLUZ - DeduzIR - Calculadora de Aproveitamento de Benefício Fiscal (Cálculo Anual)', pageWidth / 2, 40, { align: 'center' });

    // Adiciona uma linha divisória
    pdf.setLineWidth(0.5);
    pdf.line(20, 50, pageWidth - 20, 50);

    // Adiciona outras informações (opcional)
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Data: ${new Date().toLocaleDateString()}`, 20, 60);
    pdf.text(`Total: ${total?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, pageWidth - 20, 60, { align: 'right' });

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Plano: ${plano}, Remuneração Mensal: ${remuneracao?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}, Outros Rendimentos Mensais Tributáveis: ${outrosRend?.toLocaleString('pt-BR')}`, 20, 80)
  };

  const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
    if (active && payload?.length) {
      return (
        <div className="custom-tooltip" style={{ backgroundColor: '#fff', padding: '10px', border: '1px solid #ccc' }}>
          <p className="label">{`${label}`}</p>
          <p style={{ color: '#6a1b9a' }}>{`Contribuição: ${payload[0].value?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</p>
          <p style={{ color: '#ff8f00' }}>{`Valor a aportar: ${payload[1].value?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</p>
        </div>
      );
    }

    return null;
  };

  return (
    <div style={{ marginTop: "25px", display: "flex", justifyContent: "space-around" }}>
      {/* Renderiza o gráfico em um elemento oculto em mobile */}
      <div ref={chartRef} className="w-full h-[70vh]" style={isMobile ? { visibility: 'hidden', position: 'absolute' } : {}}>
        <BarChart width={910} height={height} data={data} style={{ paddingTop: "30px" }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis tick={false} />
          <Tooltip content={<CustomTooltip />} />
          <Legend content={<CustomLegend />} /> {/* ⬅️ Legenda customizada */}

          <Bar dataKey="contrib" stackId="a" fill="#6a1b9a" label={(props) => renderCustomBarLabelCont({ ...props, bgColor: "#6a1b9a", data })} />
          <Bar dataKey="restante" stackId="a" fill="#ff8f00" label={(props) => renderCustomBarLabelRest({ ...props, bgColor: "#ff8f00", data })} />

          {total > 0 && (
            <ReferenceLine
              y={total}
              stroke="green"
              strokeWidth={2.5} // Espessura maior
              label={(props) => CustomLabelGreenLine({ ...props, total, data })}
            />)}
        </BarChart>
      </div>

      {/* Renderiza o botão de download somente em mobile */}
      {isMobile && (
        <div className='container'>
          <div className="row justify-content-center">
            <button onClick={handleDownloadPDF} style={{ marginTop: '20px', padding: '10px 20px', backgroundColor: '#F08921', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>
              Baixar PDF do Gráfico
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChartIR;
